import { RefObject, useEffect, useRef } from 'react';

/**
 * Close an open dropdown on a press outside `ref` or on Esc (UI spec §9.2,
 * §9.3: the prototype's transparent backdrop). Capture-phase `mousedown` on
 * the document sees every press, including inside a modal card that stops
 * click propagation (the Repeat modal reuses these fields). The opener sits
 * inside `ref`, so its own click toggles instead of closing twice.
 */
export const useDismiss = (
  ref: RefObject<HTMLElement>,
  open: boolean,
  onDismiss: (reason: 'outside' | 'escape') => void,
) => {
  // Latest callback without re-subscribing on every render.
  const cb = useRef(onDismiss);
  cb.current = onDismiss;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (ev: MouseEvent) => {
      if (!ref.current?.contains(ev.target as Node)) cb.current('outside');
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') cb.current('escape');
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, ref]);
};
